import { useCallback, useRef } from "react";
import { create } from "zustand";

type ContainerSize = {
  width: number;
  height: number;
};

type ContainerSizeStore = {
  sizes: Record<string, ContainerSize>;
  setSize: (key: string, size: ContainerSize) => void;
  deleteSize: (key: string) => void;
};

const useContainerSizeStore = create<ContainerSizeStore>((set) => ({
  sizes: {},
  setSize: (key, size) => set((state) => ({ sizes: { ...state.sizes, [key]: size } })),
  deleteSize: (key) =>
    set((state) => {
      const sizes = { ...state.sizes };
      delete sizes[key];
      return { sizes };
    }),
}));

// Observe a container so its size is shared under `key`; any descendant can then
// switch layout via useContainerBreakpoint(key, px) without prop-drilling the size.
export function useContainerSizeObserver(key: string) {
  const setSize = useContainerSizeStore((state) => state.setSize);
  const deleteSize = useContainerSizeStore((state) => state.deleteSize);
  const ref = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      observerRef.current?.disconnect();
      ref.current = element;

      if (!element) {
        observerRef.current = null;
        deleteSize(key);
        return;
      }

      const publish = ({ width, height }: ContainerSize) => setSize(key, { width, height });

      publish(element.getBoundingClientRect()); // initial size before the observer fires
      const observer = new ResizeObserver(([entry]) => publish(entry.contentRect));
      observer.observe(element);
      observerRef.current = observer;
    },
    [key, setSize, deleteSize]
  );

  return { ref, setRef };
}

export const useContainerBreakpoint = (key: string, px: number) =>
  useContainerSizeStore((state) => (state.sizes[key]?.width ?? 0) >= px);
